import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { graphqlApi } from '../../../services/graphql';
import { useAuthStore } from '../../../store/authStore';
import { generateCampaignContractPdf } from '../../../utils/campaignContractPdf';

interface VisitInfo {
  startDate?: string;
  endDate?: string;
  adults?: number;
  children?: number;
  infants?: number;
}

interface MatchItem {
  matchId: string;
  campaignId: string;
  status: string;
  appliedAt: string;
  rewardAmount?: number;
  // Contract rendering needs these — only populated once the match has been
  // requested/accepted so they're optional on the type.
  selectedMissions?: string[];
  visitInfo?: VisitInfo;
  requestedAt?: string;
  respondedAt?: string;
  // Used to distinguish "선정된" (accepted, no content yet) from
  // "등록한" (accepted, content already submitted) per the reference
  // tab taxonomy.
  contentSubmissions?: any[];
}

interface CampaignData {
  campaignId: string;
  title?: string;
  campaignName?: string;
  description?: string;
  providedValueDescription?: string;
  campaignCategory?: string;
  category?: string;
  budgetRange?: string;
  brandName?: string;
  numberOfRecruits?: number;
  campaignType?: string;
  contentTypes?: string[];
  imageUrl?: string;
  representativeImages?: { fileUrl: string }[];
  // Fields consumed by the 캠페인 계약서 PDF generator. The Campaign row
  // carries more data than we explicitly type here; the contract helper
  // reads a broader `any` shape. These are just the ones we reference
  // directly when calling it.
  productName?: string;
  rewardAmount?: number;
  visitStartDate?: string;
  visitEndDate?: string;
  uploadDeadlineDays?: number;
  contentWritingRequirements?: string;
  publishingConditions?: any;
  secondarySubmissions?: { type: string; quantity: number; note?: string }[];
  // Used for the `D-N` recruitment deadline strip in applied/shortlisted states.
  recruitmentPeriod?: { startDate?: string; endDate?: string; alwaysRecruiting?: boolean };
  announcementEndDate?: string;
}

type FilterTab = 'all' | 'applied' | 'selected' | 'registered' | 'ended';

const MyCampaignsPage: React.FC = () => {
  const { t } = useTranslation(['profile', 'campaigns', 'common']);
  const { user } = useAuthStore();

  const [matches, setMatches] = useState<MatchItem[]>([]);
  const [campaigns, setCampaigns] = useState<Record<string, CampaignData>>({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<FilterTab>('all');

  const fetchData = useCallback(async () => {
    if (!user?.userId) return;
    try {
      setLoading(true);
      // Fetch matches
      const matchRes = await graphqlApi.getMatches({ userId: user.userId });
      const matchList: MatchItem[] = matchRes.success && matchRes.data?.matches
        ? matchRes.data.matches.map((m: any) => ({
            matchId: m.matchId, campaignId: m.campaignId,
            status: m.status, appliedAt: m.appliedAt,
            rewardAmount: m.rewardAmount,
            selectedMissions: m.selectedMissions || [],
            visitInfo: m.visitInfo || undefined,
            requestedAt: m.requestedAt || undefined,
            respondedAt: m.respondedAt || undefined,
            contentSubmissions: m.contentSubmissions || [],
          }))
        : [];
      setMatches(matchList);

      // Fetch all campaigns to get details
      const campaignRes = await graphqlApi.getCampaigns({});
      if (campaignRes.success && campaignRes.data) {
        const campaignList = (campaignRes.data as any).campaigns || campaignRes.data;
        const map: Record<string, CampaignData> = {};
        if (Array.isArray(campaignList)) {
          campaignList.forEach((c: any) => { map[c.campaignId] = c; });
        }
        setCampaigns(map);
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.userId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Predicates keyed to the 4-tab taxonomy from
  // matchgo_campaigns_final_v4.html:
  //   - applied    → 신청한 캠페인 (still under review)
  //   - selected   → 선정된 캠페인 (shortlisted / requested / accepted, no
  //                  content submitted yet)
  //   - registered → 등록한 캠페인 (accepted AND content already submitted)
  //   - ended      → 종료된 캠페인 (canceled / rejected / expired / completed)
  const hasSubmittedContent = (m: MatchItem): boolean =>
    Array.isArray(m.contentSubmissions) && m.contentSubmissions.length > 0;

  const isApplied = (m: MatchItem) => m.status === 'applied';
  const isSelected = (m: MatchItem) =>
    (m.status === 'shortlisted' || m.status === 'requested'
      || (m.status === 'accepted' && !hasSubmittedContent(m)));
  const isRegistered = (m: MatchItem) =>
    m.status === 'accepted' && hasSubmittedContent(m);
  const isEnded = (m: MatchItem) =>
    m.status === 'canceled' || m.status === 'rejected' || m.status === 'not_selected'
    || m.status === 'expired' || m.status === 'completed';

  const getFiltered = () => {
    switch (activeTab) {
      case 'applied': return matches.filter(isApplied);
      case 'selected': return matches.filter(isSelected);
      case 'registered': return matches.filter(isRegistered);
      case 'ended': return matches.filter(isEnded);
      case 'all':
      default:
        return matches;
    }
  };

  const counts = {
    all: matches.length,
    applied: matches.filter(isApplied).length,
    selected: matches.filter(isSelected).length,
    registered: matches.filter(isRegistered).length,
    ended: matches.filter(isEnded).length,
  };

  const filtered = getFiltered();
  const tabs: { key: FilterTab; labelKey: string }[] = [
    { key: 'all', labelKey: 'myProfile.myCampaigns.tabs.all' },
    { key: 'applied', labelKey: 'myProfile.myCampaigns.tabs.applied' },
    { key: 'selected', labelKey: 'myProfile.myCampaigns.tabs.selected' },
    { key: 'registered', labelKey: 'myProfile.myCampaigns.tabs.registered' },
    { key: 'ended', labelKey: 'myProfile.myCampaigns.tabs.ended' },
  ];

  const krwFormatter = new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW' });

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <div className="pt-6 mb-6">
        <h2 className="text-2xl font-semibold text-gray-800 mb-2">
          {t('profile:myProfile.myCampaigns.title')}
        </h2>
        <hr className="border-gray-200" />
      </div>

      {/* Filter chips — matches the `sub-filter` pill treatment from
         matchgo_campaigns_final_v4.html. Smaller pills with a hairline
         border, indigo fill when active, and an inline count badge next
         to the label. Dividers between chips mirror the reference's
         vertical bar separators. */}
      <div className="mb-6 flex flex-wrap items-center gap-1.5">
        {tabs.map((tab, idx) => {
          const isActive = activeTab === tab.key;
          const count = counts[tab.key];
          return (
            <React.Fragment key={tab.key}>
              <button
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors ${
                  isActive
                    ? 'border-indigo-600 bg-indigo-600 font-medium text-white'
                    : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <span>{t(`profile:${tab.labelKey}`)}</span>
                <span
                  className={`text-[11px] ${isActive ? 'text-white/80' : 'text-gray-400'}`}
                >
                  {count}
                </span>
              </button>
              {idx < tabs.length - 1 && (
                <span className="h-3.5 w-px bg-gray-200" aria-hidden />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          {t('profile:myProfile.myCampaigns.empty')}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((match) => {
            const c = campaigns[match.campaignId];
            const imgUrl = c?.representativeImages?.[0]?.fileUrl || c?.imageUrl;
            const title = c?.campaignName || c?.title || '';
            const desc = c?.providedValueDescription || c?.description || '';
            return (
              <div key={match.matchId}
                className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow overflow-hidden">
                {/* Image Header */}
                <div className="relative h-48 overflow-hidden">
                  {imgUrl ? (
                    <>
                      <img src={imgUrl} alt={title} className="absolute inset-0 w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black bg-opacity-30" />
                    </>
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-purple-600" />
                  )}
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-gray-800 text-xs font-semibold rounded-full shadow-sm">
                      {(() => {
                        // Resolve the category label. Historically the
                        // `campaignCategory` field has carried two flavours
                        // of keys depending on when the campaign was
                        // created:
                        //   1. Catalog aliases (`accommodation | restaurant
                        //       | cafe | tour | ticket | shopping |
                        //       wellness | other`) — resolved through
                        //      `common:categories.*`.
                        //   2. Registration enum (`hotel | fnb |
                        //       tour_activity | spa_wellness |
                        //       aviation_transport | other`) — resolved
                        //      through `campaigns:registration.categories.*`
                        //      with the legacy camelCase key map.
                        // `category` is a legacy field that only ever held
                        // a catalog alias.
                        const rawKey = c?.campaignCategory || c?.category;
                        if (!rawKey) return '';

                        const catalogAliases = new Set([
                          'accommodation', 'restaurant', 'cafe', 'tour',
                          'ticket', 'shopping', 'wellness', 'other',
                        ]);
                        if (catalogAliases.has(rawKey)) {
                          return t(`common:categories.${rawKey}`, rawKey);
                        }

                        const registrationKeyMap: Record<string, string> = {
                          hotel: 'hotel',
                          fnb: 'fnb',
                          tour_activity: 'tourActivity',
                          spa_wellness: 'spaWellness',
                          aviation_transport: 'aviationTransport',
                          other: 'other',
                        };
                        const mapped = registrationKeyMap[rawKey];
                        if (mapped) {
                          return t(
                            `campaigns:registration.categories.${mapped}`,
                            rawKey,
                          );
                        }

                        // Unknown key — render as-is.
                        return rawKey;
                      })()}
                    </span>
                  </div>
                  <div className="absolute top-4 right-4">
                    <span className={`px-3 py-1 text-xs font-semibold rounded-full shadow-sm ${
                      match.status === 'applied' ? 'bg-gray-100 text-gray-700' :
                      match.status === 'shortlisted' ? 'bg-indigo-100 text-indigo-700' :
                      match.status === 'requested' ? 'bg-yellow-100 text-yellow-700' :
                      match.status === 'accepted' ? 'bg-green-100 text-green-700' :
                      match.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                      match.status === 'expired' ? 'bg-orange-100 text-orange-700' :
                      match.status === 'not_selected' ? 'bg-gray-100 text-gray-500' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {t(`profile:myProfile.myCampaigns.status.${match.status}`)}
                    </span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                    <h3 className="text-lg font-semibold text-white line-clamp-2">{title}</h3>
                  </div>
                </div>

                {/* Card Content */}
                <div className="p-6">
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">{desc}</p>
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    {c?.brandName && (
                      <span className="flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5" />
                        </svg>
                        {c.brandName}
                      </span>
                    )}
                    {c?.numberOfRecruits != null && (
                      <span>{c.numberOfRecruits}명 모집</span>
                    )}
                  </div>
                  {c?.contentTypes && c.contentTypes.length > 0 && (
                    <div className="flex gap-2 mb-4 flex-wrap">
                      {c.contentTypes.slice(0, 3).map((ct, idx) => {
                        const keyMap: Record<string, string> = {
                          instagram_post: 'instagramPost', instagram_reels: 'instagramReels',
                          youtube: 'youtube', tiktok: 'tiktok', naver_blog: 'naverBlog',
                        };
                        return (
                          <span key={idx} className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs rounded">
                            {t(`campaigns:registration.contentTypes.${keyMap[ct] || ct}`, ct)}
                          </span>
                        );
                      })}
                    </div>
                  )}
                  {/* Reward */}
                  {/* === card-meta row + optional deadline-inline ===
                     Mirrors the reference HTML: compact `신청일 YYYY. M. D.`
                     and `리워드 : <amount>` row, plus an optional D-N
                     deadline strip sourced from the most relevant date
                     for the current status. */}
                  {(() => {
                    // Format a YYYY. M. D. date string in Korean (matches
                    // the reference `2026. 4. 25.`) using toLocaleDateString.
                    const fmtDate = (iso?: string | null): string => {
                      if (!iso) return '';
                      const d = new Date(iso);
                      if (Number.isNaN(d.getTime())) return '';
                      return d.toLocaleDateString('ko-KR', {
                        year: 'numeric', month: 'numeric', day: 'numeric',
                      });
                    };

                    // Compute days remaining from now to the given ISO
                    // date, rounded DOWN to whole days (matches D-N UX).
                    // Negative numbers indicate the deadline has passed.
                    const daysUntil = (iso?: string | null): number | null => {
                      if (!iso) return null;
                      const end = new Date(iso);
                      if (Number.isNaN(end.getTime())) return null;
                      const msPerDay = 1000 * 60 * 60 * 24;
                      return Math.ceil((end.getTime() - Date.now()) / msPerDay);
                    };

                    // Pick the deadline that's relevant to the current
                    // match status, following the reference HTML cues:
                    //   - applied / shortlisted  → recruitment end (모집 마감)
                    //   - requested              → announcement end (수락 마감)
                    //   - accepted               → visit end + upload deadline
                    //                              (콘텐츠 제출 완료까지)
                    //   - others                 → no deadline strip
                    let deadline: { label: string; iso: string } | null = null;
                    const recruitmentEnd = c?.recruitmentPeriod?.endDate;
                    const alwaysRecruiting = c?.recruitmentPeriod?.alwaysRecruiting;
                    if (match.status === 'applied' || match.status === 'shortlisted') {
                      if (recruitmentEnd && !alwaysRecruiting) {
                        deadline = { label: '모집 마감', iso: recruitmentEnd };
                      }
                    } else if (match.status === 'requested') {
                      const ann = c?.announcementEndDate;
                      if (ann) deadline = { label: '수락 마감', iso: ann };
                    } else if (match.status === 'accepted') {
                      const visitEnd = c?.visitEndDate;
                      const days = c?.uploadDeadlineDays;
                      if (visitEnd && days) {
                        const submitBy = new Date(visitEnd);
                        submitBy.setDate(submitBy.getDate() + days);
                        deadline = { label: '제출 완료까지', iso: submitBy.toISOString() };
                      }
                    }
                    const dn = daysUntil(deadline?.iso);

                    return (
                      <>
                        {deadline && dn !== null && dn >= 0 && (
                          <div className="mb-3 flex items-center gap-1.5 rounded-md border border-orange-200 bg-orange-50 px-2.5 py-1.5 text-[11px] text-orange-700">
                            <svg className="h-3 w-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                              <circle cx="12" cy="12" r="10" />
                              <line x1="12" y1="8" x2="12" y2="12" />
                              <line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                            <span>
                              {deadline.label}{' '}
                              <strong className="mx-0.5">{dn === 0 ? 'D-Day' : `D-${dn}`}</strong>
                            </span>
                          </div>
                        )}
                        <div className="mb-4 flex items-center justify-between text-xs text-gray-500">
                          <span>
                            {t('profile:myProfile.myCampaigns.appliedAt')} {fmtDate(match.appliedAt)}
                          </span>
                          <span>
                            {t('profile:myProfile.myCampaigns.reward', '리워드')} :{' '}
                            {typeof match.rewardAmount === 'number'
                              ? krwFormatter.format(match.rewardAmount)
                              : '—'}
                          </span>
                        </div>
                      </>
                    );
                  })()}
                  {/* Status message — guided notice block, colored per state.
                     Mirrors the `notice` + `n-dot` pattern from the design
                     reference (matchgo_campaigns_final_v4.html). */}
                  {(() => {
                    // Map match.status → one of 5 notice palettes.
                    const notice = {
                      applied:     { bg: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-900',   dot: 'bg-amber-500'  },
                      shortlisted: { bg: 'bg-violet-50',  border: 'border-violet-200',  text: 'text-violet-900',  dot: 'bg-violet-500' },
                      requested:   { bg: 'bg-amber-50',   border: 'border-amber-300',   text: 'text-amber-900',   dot: 'bg-amber-600'  },
                      accepted:    { bg: 'bg-green-50',   border: 'border-green-200',   text: 'text-green-900',   dot: 'bg-green-500'  },
                      canceled:    { bg: 'bg-red-50',     border: 'border-red-200',     text: 'text-red-900',     dot: 'bg-red-500'    },
                      rejected:    { bg: 'bg-red-50',     border: 'border-red-200',     text: 'text-red-900',     dot: 'bg-red-500'    },
                      not_selected:{ bg: 'bg-gray-50',    border: 'border-gray-200',    text: 'text-gray-700',    dot: 'bg-gray-400'   },
                      expired:     { bg: 'bg-orange-50',  border: 'border-orange-200',  text: 'text-orange-900',  dot: 'bg-orange-500' },
                      completed:   { bg: 'bg-blue-50',    border: 'border-blue-200',    text: 'text-blue-900',    dot: 'bg-blue-500'   },
                    }[match.status];
                    if (!notice) return null;
                    return (
                      <div
                        className={`mb-4 flex items-start gap-2 rounded-lg border ${notice.bg} ${notice.border} ${notice.text} px-3 py-2.5 text-xs leading-relaxed`}
                      >
                        <span className={`mt-[5px] inline-block h-2 w-2 flex-shrink-0 rounded-full ${notice.dot}`} />
                        <span>
                          {t(`profile:myProfile.myCampaigns.statusMessage.${match.status}`)}
                        </span>
                      </div>
                    );
                  })()}
                  {/* Application Status — intentionally removed. Status
                     is already surfaced by the header badge on the card
                     thumbnail and the colored `notice` block above, so a
                     third row here was redundant. The compact card-meta
                     row below carries 신청일 and 리워드. */}
                  {match.status === 'requested' && (
                    <Link
                      to={`/my-profile/campaigns/accept/${match.matchId}`}
                      className="block w-full text-center px-4 py-2 mb-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-colors"
                    >
                      {t('profile:myProfile.myCampaigns.confirmCampaign', '캠페인 진행 확정')}
                    </Link>
                  )}
                  {match.status === 'accepted' && (
                    <Link
                      to={`/my-profile/campaigns/submit/${match.matchId}`}
                      className="block w-full text-center px-4 py-2 mb-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium transition-colors"
                    >
                      {t('profile:myProfile.myCampaigns.submitContent', '콘텐츠 제출')}
                    </Link>
                  )}
                  {(match.status === 'accepted' || match.status === 'completed') && (
                    <button
                      type="button"
                      onClick={() => {
                        // Render the 캠페인 계약서 PDF for this match. Requires
                        // publishingConditions from the Campaign and selectedMissions
                        // + visitInfo captured when the influencer accepted.
                        if (!c) return;
                        const visit = match.visitInfo || {};
                        generateCampaignContractPdf({
                          campaign: c,
                          userName: user?.name || '',
                          selectedMissions: match.selectedMissions || [],
                          visitStartDate: visit.startDate || c.visitStartDate || '',
                          visitEndDate: visit.endDate || c.visitEndDate || '',
                          requestedAt: match.requestedAt,
                          respondedAt: match.respondedAt,
                        });
                      }}
                      className="block w-full text-center px-4 py-2 mb-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                    >
                      {t('profile:myProfile.myCampaigns.viewContract', '캠페인 계약서 보기')}
                    </button>
                  )}
                  <Link
                    to={`/campaigns/${match.campaignId}`}
                    className="block w-full text-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
                  >
                    {t('profile:myProfile.myCampaigns.viewDetails', 'View Details')}
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyCampaignsPage;
